/**
 * Right column of the Messages page: one contact's header (WhatsApp / Call /
 * Log their reply / Import WhatsApp chat / Contacts), their timeline, and the
 * composer.
 *
 * The timeline is the same one lead detail shows for this person: messages
 * and every lead's activities, merged (see `ContactTimeline`).
 */

import {
	ArrowLeft,
	FileUp,
	MessageCircle,
	MessageSquareReply,
	Phone,
	Users,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ContactInitials } from "@/features/realtor/components/contact-initials";
import {
	type Contact,
	telUrl,
	whatsappUrl,
} from "@/features/realtor/services/realtor-crm-api.service";
import { ImportChatDialog } from "../import/import-chat-dialog";
import { ContactTimeline } from "../timeline/contact-timeline";
import { LogReplyDialog } from "../timeline/log-reply-dialog";
import { Composer } from "./composer";

const headerLinkClass =
	"inline-flex h-8 items-center gap-1.5 rounded-lg border border-dashboard-border bg-dashboard-bg px-2.5 text-xs font-medium text-dashboard-text transition-colors hover:bg-dashboard-surface";

/**
 * For the two links whose href carries the contact's phone number.
 * Session replay masks text nodes and never attributes, so those two are
 * blocked from the recording entirely. The third link sharing
 * `headerLinkClass` is plain navigation and must NOT be blocked — doing
 * so would blank part of every replay for no privacy gain.
 */
const phoneLinkClass = `ph-no-capture ${headerLinkClass}`;

export function Conversation({
	contact,
	onBack,
}: {
	contact: Contact;
	onBack: () => void;
}) {
	const { t } = useTranslation();
	const [loggingReply, setLoggingReply] = useState(false);
	const [importing, setImporting] = useState(false);
	const wa = whatsappUrl(contact.phoneCountryCode, contact.phone);
	const tel = telUrl(contact.phoneCountryCode, contact.phone);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<header className="flex flex-wrap items-center gap-3 border-b border-dashboard-border px-3 py-3 sm:px-4">
				<button
					type="button"
					onClick={onBack}
					className="-ml-1 rounded-md p-1 text-dashboard-muted hover:text-dashboard-text md:hidden"
					aria-label={t("realtor.messages.back")}
				>
					<ArrowLeft className="h-4 w-4" />
				</button>
				<Avatar className="h-9 w-9 shrink-0 border border-dashboard-border">
					<AvatarFallback className="bg-dashboard-border text-[11px] font-semibold text-dashboard-text">
						<ContactInitials name={contact.name} />
					</AvatarFallback>
				</Avatar>
				<div className="min-w-0 flex-1">
					<h2 className="truncate font-display text-lg italic font-medium text-dashboard-text">
						{contact.name}
					</h2>
					<p className="text-[11px] text-dashboard-muted">
						{t(`realtor.messages.contactTypes.${contact.type}`)}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{wa && (
						<a
							href={wa}
							target="_blank"
							rel="noopener noreferrer"
							className={phoneLinkClass}
							aria-label={t("realtor.messages.whatsappAria", {
								name: contact.name,
							})}
						>
							<MessageCircle className="h-3.5 w-3.5" />
							{t("realtor.messages.whatsapp")}
						</a>
					)}
					{tel && (
						<a
							href={tel}
							className={phoneLinkClass}
							aria-label={t("realtor.messages.callAria", {
								name: contact.name,
							})}
						>
							<Phone className="h-3.5 w-3.5" />
							{t("realtor.messages.call")}
						</a>
					)}
					<button
						type="button"
						onClick={() => setLoggingReply(true)}
						className={headerLinkClass}
					>
						<MessageSquareReply className="h-3.5 w-3.5" />
						{t("realtor.timeline.logReply.action")}
					</button>
					<button
						type="button"
						onClick={() => setImporting(true)}
						className={headerLinkClass}
					>
						<FileUp className="h-3.5 w-3.5" />
						{t("realtor.import.action")}
					</button>
					<Link to="/realtor/contacts" className={headerLinkClass}>
						<Users className="h-3.5 w-3.5" />
						{t("realtor.messages.goToContacts")}
					</Link>
				</div>
				{!wa && (
					<p className="w-full text-[11px] text-dashboard-muted">
						{t("realtor.messages.noPhoneHint")}
					</p>
				)}
			</header>

			<ContactTimeline
				key={contact.id}
				contactId={contact.id}
				contactName={contact.name}
				emptyText={t("realtor.messages.threadEmpty", { name: contact.name })}
				className="flex-1"
			/>

			<Composer key={contact.id} contact={contact} />

			<LogReplyDialog
				open={loggingReply}
				onOpenChange={setLoggingReply}
				contactId={contact.id}
				contactName={contact.name}
			/>

			<ImportChatDialog
				open={importing}
				onOpenChange={setImporting}
				contact={{ id: contact.id, name: contact.name }}
			/>
		</div>
	);
}
